import React, { useState, useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Select } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import useGetAllRolesLight from "../../../customHooks/GET_requests/useGetAllRolesLight";
import { formatUuidT } from "../../../flatbuffers/display";
// MASTER-ORG ROLE MARKERS — same star/shield pair used by the Roles table and
// the create/edit role forms, so a LiftNet role reads the same way here.
import { isMasterOrgId } from "../../../utils/masterOrganization";
import MasterRoleStar from "../../../components/roles/MasterRoleStar";
import TenantRoleShield from "../../../components/roles/TenantRoleShield";
// INTERFACES
import { RoleLightInterface } from "../../../interfaces/Roles.interface";
import { SelectRoleInterface } from "../../../interfaces/Global.interface";
import { RootState } from "../../../app/store";

interface OrganizationGroup {
  id: unknown;
  name: string;
}

function SelectRolesLight({ value, onChangeIdRole, disabledInput }: SelectRoleInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Option, OptGroup } = Select;
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [organizationGroups, setOrganizationGroups] = useState<OrganizationGroup[]>([]);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { rolesLight: roles, isLoadingGetAllRolesLight } = useGetAllRolesLight({
    orgId: globalOrganizationId,
  });

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const normalizeId = (idValue: unknown): string => {
    if (typeof idValue === "string" || typeof idValue === "number") return String(idValue);
    if (!idValue) return "";
    if (typeof idValue === "object") return formatUuidT(idValue as any) || String(idValue);
    return String(idValue);
  };

  const uniqueOrganizations = () => {
    const d: OrganizationGroup[] = roles
      .map((item) => item.organization)
      .filter(
        (org, index, self) =>
          org && index === self.findIndex((o) => o && normalizeId(o.id) === normalizeId(org.id)),
      )
      // LiftNet/MASTER organization groups surface first — Array#sort is
      // stable, so the fetch order is kept within each group (master /
      // non-master).
      .sort(
        (a, b) =>
          Number(isMasterOrgId(normalizeId(b.id))) - Number(isMasterOrgId(normalizeId(a.id))),
      );
    setOrganizationGroups(d);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    uniqueOrganizations();
  }, [roles]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const selectedValue = normalizeId(value);
  const valueInOptions = roles.some((r) => normalizeId(r.id) === selectedValue);
  // Mirrors SelectOrganizationsLight's valueExistsInOptions guard: when the
  // selected role isn't in the currently-loaded org's role list (e.g. right
  // after switching org, before the reset in YourProfileDrawer/
  // GlobalConfiguration's onChangeIdOrganization lands), fall back to
  // showing nothing rather than a role that belongs to a different org —
  // never fabricate an option to keep a stale cross-org role looking valid.
  const displayValue = valueInOptions ? selectedValue : undefined;

  return (
    <Select
      className="generalStyles__width100"
      disabled={disabledInput}
      showSearch
      placeholder={t("report.selectBank")}
      value={displayValue}
      size="small"
      onChange={onChangeIdRole}
      loading={isLoadingGetAllRolesLight}
      // `label` holds the searchable plain name; the children render the
      // star/shield + name. antd can only substring-search a string —
      // filtering on the star/shield-bearing node would never match what
      // the operator types (same pattern as the role-base picker).
      filterOption={(input: string, option: any) =>
        String(option?.label ?? "")
          .toLowerCase()
          .includes(input.toLowerCase())
      }
    >
      {organizationGroups.map((group: OrganizationGroup) => (
        <OptGroup key={normalizeId(group.id)} label={`ORGANIZATION / ${String(group.name ?? "")}`}>
          {roles
            .filter(
              (role: RoleLightInterface) =>
                role &&
                role.organization &&
                normalizeId(role.organization.id) === normalizeId(group.id),
            )
            .map((server: RoleLightInterface) => {
              const isMaster = isMasterOrgId(normalizeId(server.organization?.id));
              const name = String(server.name ?? "");
              return (
                <Option key={normalizeId(server.id)} value={normalizeId(server.id)} label={name}>
                  {/* Same star/shield pair as the Roles table: the master
                      star for a LiftNet/MASTER role, the tenant shield for
                      everything else — every option carries one marker. */}
                  <MasterRoleStar show={isMaster} />
                  <TenantRoleShield show={!isMaster} />
                  {name}
                </Option>
              );
            })}
        </OptGroup>
      ))}
    </Select>
  );
}

export default SelectRolesLight;
